import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import FooterCertificates from "../components/FooterCertificates";
import Footer from "../components/Footer";
import Nav from "../components/Nav.jsx";
import { useLanguage } from "../config/LanguageContext.jsx";

const IndustryPage = ({
  data,
  heroImage,
  iconMapping,
  imageMapping,
  translationKey,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const landingRef = useRef(null);
  const navRef = useRef(null);
  const originalNavOffset = useRef(0);
  const sectionRefs = useRef({});
  const { currentLanguage, translations } = useLanguage();

  useEffect(() => {
    if (navRef.current) {
      originalNavOffset.current = navRef.current.offsetTop;
    }
  }, []);

  const handleScroll = () => {
    const currentScrollPos = window.pageYOffset;
    setIsSticky(currentScrollPos >= originalNavOffset.current);

    for (const [key, ref] of Object.entries(sectionRefs.current)) {
      if (ref && ref.getBoundingClientRect().top <= 100) {
        setActiveSection(key);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (event, targetId) => {
    event.preventDefault();
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const featuresList = data?.featuresList || [];

  return (
    <>
      <Nav />
      <div className="industries-section" ref={landingRef}>
        {/* Landing Section */}
        <div className="industry-landing-container">
          <div>
            <h1>{data.title[currentLanguage]}</h1>
            <h2>{data.subtitle[currentLanguage]}</h2>
            <Link to="/bookdemo">
              <button className="btn-white">
                {
                  {
                    en: "Request a demo",
                    fr: "Réserver une démo",
                    de: "Demo anfordern",
                    es: "Solicitar una demo",
                  }[currentLanguage]
                }
              </button>
            </Link>{" "}
          </div>
          <img
            src={heroImage}
            alt={data.title}
            className="industry-landing-image"
          />
        </div>

        {/* Sticky Navigation */}
        <div
          ref={navRef}
          className={`industry-nav-container ${isSticky ? "sticky" : ""}`}
        >
          <ul>
            {featuresList.map((feature, index) => (
              <li
                key={index}
                className={
                  activeSection === feature.link.replace("#", "")
                    ? "active-nav-item"
                    : ""
                }
              >
                <a
                  href={feature.link}
                  onClick={(e) =>
                    handleNavClick(e, feature.link.replace("#", ""))
                  }
                >
                  {/* Use the translation based on the current language */}
                  {feature.name[currentLanguage] || feature.name["en"]}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <h3 className="industry-h3">
          {
            {
              en: "Key Features",
              fr: "Fonctionnalités clés",
              de: "Wichtigste Funktionen",
              es: "Funciones clave",
            }[currentLanguage]
          }
        </h3>

        {featuresList.map((feature, index) => {
          const featureKey = feature.link.replace("#", "");
          const section = data[featureKey];

          if (!section) return null;

          return (
            <section
              key={index}
              id={featureKey}
              ref={(el) => (sectionRefs.current[featureKey] = el)}
              className={`industry-feature-container ${
                index % 2 !== 0 ? "reversed" : ""
              }`}
            >
              <div className="industry-feature-context-container">
                <div className="industry-feature-context">
                  {/* Translate section.title and section.subTitle based on currentLanguage */}
                  <h2>{section.title[currentLanguage]}</h2>
                  <h3>{section.subTitle[currentLanguage]}</h3>

                  {/* Render translated text */}
                  {section.text[currentLanguage].map((paragraph, idx) =>
                    typeof paragraph === "string" ? (
                      <p key={idx}>{paragraph}</p>
                    ) : (
                      <ul key={idx}>
                        {paragraph.list.map((item, listIdx) => (
                          <li key={listIdx}>{item}</li>
                        ))}
                      </ul>
                    )
                  )}
                </div>

                <div
                  className="industry-feature-image-container"
                  style={{
                    backgroundImage: `url(${
                      imageMapping[section.image] || ""
                    })`,
                  }}
                ></div>
              </div>

              <div className="industry-icons-container">
                {section.icons.map((iconData, idx) => {
                  const IconComponent = iconMapping[iconData.icon];
                  return (
                    <div key={idx} className="industry-feature">
                      <div>
                        <IconComponent color={"#1c2f3e"} />
                      </div>
                      <span>{iconData.text[currentLanguage]}</span>{" "}
                      {/* Use translation */}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <FooterCertificates />
      <Footer />
    </>
  );
};

export default IndustryPage;
